import { Component, OnInit, ViewChild, signal, EventEmitter, Output, Input, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { ClienteService } from '../../../services/cliente/cliente.service';
import { Cliente } from '../../../domain/cliente.domain';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-anunciante-link-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule
  ],
  templateUrl: './anunciante-link-dialog.component.html',
  styleUrls: ['./anunciante-link-dialog.component.scss']
})
export class AnuncianteLinkDialogComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  @Input() padreId: string = '';
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() anuncianteLinked = new EventEmitter<void>();

  @ViewChild('dt') table: Table | undefined;

  private clienteService = inject(ClienteService);
  private messageService = inject(MessageService);
  private cdr = inject(ChangeDetectorRef);

  anunciantes = signal<Cliente[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  filterValue = signal<string>('');
  selectedTab = signal<'libres' | 'todos'>('libres');
  isLoadingRequest = signal<boolean>(false); // Flag para evitar múltiples requests simultáneos
  blockLazyLoad = signal<boolean>(false); // Flag para bloquear onLazyLoad mientras actualizamos datos

  nroFilasPorPagina = 10;
  paginaActual = 0;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  ngOnInit(): void {
    // Configurar debounce para búsqueda
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Cargar directamente sin usar table.reset() para evitar múltiples eventos
      const lazyEvent: TableLazyLoadEvent = {
        first: 0,
        rows: this.nroFilasPorPagina,
        sortField: undefined,
        sortOrder: undefined,
        filters: {},
        multiSortMeta: []
      };
      this.loadAnunciantes(lazyEvent);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
    if (visible) {
      this.selectedTab.set('libres');
      this.filterValue.set('');
      // Limpiar datos antes de cargar nuevos
      this.anunciantes.set([]);
      this.totalRecords.set(0);
      this.isLoadingRequest.set(false);
      this.blockLazyLoad.set(false);
      // Cargar directamente sin usar table.reset() para evitar múltiples eventos
      setTimeout(() => {
        const lazyEvent: TableLazyLoadEvent = {
          first: 0,
          rows: this.nroFilasPorPagina,
          sortField: undefined,
          sortOrder: undefined,
          filters: {},
          multiSortMeta: []
        };
        this.loadAnunciantes(lazyEvent);
      }, 200);
    } else {
      this.filterValue.set('');
      this.selectedTab.set('libres');
      // Limpiar datos al cerrar
      this.anunciantes.set([]);
      this.totalRecords.set(0);
      this.isLoadingRequest.set(false);
      this.blockLazyLoad.set(false);
    }
  }

  onTabChange(tab: 'libres' | 'todos'): void {
    this.selectedTab.set(tab);
    // Cargar directamente sin usar table.reset() para evitar múltiples eventos
    const lazyEvent: TableLazyLoadEvent = {
      first: 0,
      rows: this.nroFilasPorPagina,
      sortField: undefined,
      sortOrder: undefined,
      filters: {},
      multiSortMeta: []
    };
    this.loadAnunciantes(lazyEvent);
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.filterValue.set(value);
    this.searchSubject.next(value);
  }

  loadAnunciantes(event?: TableLazyLoadEvent): void {
    // Prevenir múltiples requests simultáneos - establecer flag de forma síncrona
    if (this.isLoadingRequest()) {
      return;
    }

    // Establecer flags de forma síncrona antes de cualquier operación asíncrona
    this.isLoadingRequest.set(true);
    this.blockLazyLoad.set(true); // Bloquear onLazyLoad mientras cargamos
    this.loading.set(true);

    const first = event?.first ?? 0;
    const page = Math.floor(first / this.nroFilasPorPagina) + 1;
    const size = event?.rows ?? this.nroFilasPorPagina;

    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('tipoCliente', 'anunciante')
      .set('_order[razonSocial]', 'asc');

    // Filtro: libres o todos usando ExistsFilter
    if (this.selectedTab() === 'libres') {
      // Filtrar anunciantes donde agenciaProductor es null (libres)
      params = params.set('exists[agenciaProductor]', 'false');
    }

    // Filtro global
    const filterValue = this.filterValue();
    if (filterValue?.trim()) {
      params = params.set('q', filterValue.trim());
    }

    this.clienteService.searchClientesCollection(params).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // Verificar que aún estamos en el mismo estado antes de procesar
        if (!this.isLoadingRequest()) {
          return;
        }

        const items = response.members;
        const totalItemsFromServer = response.totalItems;
        
        // El filtrado se hace en el backend usando ExistsFilter, así que usamos los datos directamente
        this.anunciantes.set(items);
        this.totalRecords.set(totalItemsFromServer);
        this.loading.set(false);
        this.isLoadingRequest.set(false);
        
        // Desbloquear onLazyLoad después de un pequeño delay para asegurar que PrimeNG procese los cambios
        setTimeout(() => {
          this.blockLazyLoad.set(false);
          this.cdr.detectChanges();
        }, 100);
      },
      error: (error) => {
        console.error('Error al cargar anunciantes:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los anunciantes',
          life: 3000
        });
        this.loading.set(false);
        this.isLoadingRequest.set(false);
        this.blockLazyLoad.set(false);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    // Bloquear si estamos cargando o si blockLazyLoad está activo
    if (this.isLoadingRequest() || this.blockLazyLoad()) {
      return;
    }
    this.loadAnunciantes(event);
  }

  linkAnunciante(anunciante: Cliente): void {
    if (!anunciante.id || !this.padreId) {
      return;
    }

    // Verificar si ya está vinculado al padre actual
    if (this.isAlreadyLinked(anunciante)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Este anunciante ya está vinculado a esta agencia/productor',
        life: 3000
      });
      return;
    }

    this.loading.set(true);
    this.clienteService.linkAnunciante(anunciante.id, this.padreId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.loading.set(false);
        this.anuncianteLinked.emit();
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Anunciante vinculado correctamente',
          life: 3000
        });
      },
      error: (error) => {
        console.error('Error al vincular anunciante:', error);
        this.loading.set(false);
        let errorMessage = 'Error al vincular el anunciante';
        if (error.error?.violations) {
          const violations = error.error.violations;
          if (violations.length > 0) {
            errorMessage = violations[0].message || errorMessage;
          }
        } else if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000
        });
      }
    });
  }

  isAlreadyLinked(anunciante: Cliente): boolean {
    if (!this.padreId || !anunciante.agenciaProductor) {
      return false;
    }

    if (typeof anunciante.agenciaProductor === 'string') {
      const match = anunciante.agenciaProductor.match(/\/(\d+)$/);
      return match ? match[1] === this.padreId : false;
    }

    if (typeof anunciante.agenciaProductor === 'object' && anunciante.agenciaProductor?.id) {
      return anunciante.agenciaProductor.id.toString() === this.padreId;
    }

    return false;
  }

  canLink(anunciante: Cliente): boolean {
    return !this.isAlreadyLinked(anunciante) && !this.loading();
  }

}

